// import type { PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import { PsHttpSSE as PsHttpSSEOfficial } from '@patsnap-utils/sse';
import type { ExtractFeatureResult } from './types/extract-feature-result';
import type { CreateTaskRequest, CreateTaskResponse, TaskExistsRequest, TaskExistsResponse } from './types/task';
import type { FtoProjectRequest, FtoProjectResponse } from './types/fto-project';
import type { RetrievalQueryResponse, RetrievalQueriesResponse } from './types/patent-query';
import type { ExpandedKeywordsRequest, ExpandedKeywordsResponse, CompleteKeywordsRequest, CompleteKeywordsResponseData } from './types/expanded-keywords';
import type { FtoReportListRequest, FtoReportListResponse, FtoReportResponse } from './types/fto-report';
import type { PatentCountResponse } from './types/patent-count';
import type { PatentProfile } from '@/services/patent-service/patent-profile-service';
import { PatentRiskLevel } from '../enums';

// 简化版特征抽取请求接口
export interface ExtractFeatureRequest {
    /** 任务ID */
    task_id: string;
    /** 操作类型 */
    operate_type?: string;
    /** 技术方案描述，必填且非空 */
    text?: string;
    /** 控制token消耗量 */
    cc_num?: number;
    /** 其他文本 */
    other_text?: string;
}

// 技术概要接口
export interface TechSummary {
    /** 技术概要内容 */
    summary: string;
    /** 技术概要标题 */
    title: string;
    /** 技术概要描述 */
    description: string;
    /** 技术概要核心特征 */
    core_features: string[];
    /** 技术概要组件 */
    components: {
        name: string;
        type: string;
        function: string;
        specification: string;
        core: boolean;
    }[];
    /** 技术概要流程 */
    processes: {
        sequence: number;
        name: string;
        description: string;
        inputs: string[];
        outputs: string[];
        conditions: string[];
    }[];
    /** 技术概要参数 */
    parameters: {
        name: string;
        value: string;
        unit: string;
        range: string;
        type: string;
    }[];
    /** 技术领域 */
    field: string;
    /** 技术要点 */
    key_points: string[];
    /** 技术应用 */
    application: string;
    /** 技术领域 */
    domain: string;
    /** 技术替代方案 */
    alternatives: string[];
    /** 相关IPC分类 */
    ipc_codes: string[];
    /** 整体置信度 */
    confidence: number;
    /** 技术问题 */
    problem: string;
}

// 专利风险点接口
export interface PatentRisk {
    /** 风险点描述 */
    description: string;
    /** 风险等级 */
    risk_level: PatentRiskLevel;
    /** 相关技术领域 */
    related_fields: string[];
}

// 专利搜索工具请求接口
export interface PatentSearchToolRequest {
    /** 任务ID */
    task_id: string;
    /** 操作类型 */
    operate_type?: string;
    /** 输入内容 */
    text?: string;
    /** 控制token消耗量 */
    cc_num?: number;
    /** 搜索索引，默认0 */
    search_index?: number;
    /** 工具类型 */
    tool_type?: string;
}

// 专利文档接口
export interface PatentDoc {
    /** 专利ID */
    _id: string;
    /** 专利标题 */
    TITLE: string;
    SORT_SCORE: number;
    ANCS: string[];
    PBD: string;
    PN_STR: string;
    patent_id: string;
    remove_at_step: number;
    score: number;
    /**
     * 当 {@link PatentSearchResponse} 的 search_type 为 CUSTOM_QUERY 时，表示该专利是否被选中.
     */
    selected: boolean;
}

// 专利搜索响应接口
export interface PatentSearchResponse {
    /** 搜索类型 */
    search_type: 'NUMBER_TEXT' | 'CUSTOM' | 'CUSTOM_QUERY';
    /** 生成的查询语句 */
    generated_query: string;
    /** 专利文档列表 */
    patents: PatentDoc[];
    /** 搜索结果总数 */
    total_count: number;
    /** 搜索耗时（毫秒） */
    search_time_ms: number;
}

// 用户自定义检索请求接口
export interface CustomSearchRequest {
    /** 任务ID */
    task_id: string;
    /** 搜索索引，必填，最小值0 */
    search_index: number;
    /** 用户自定义查询语句，必填且非空 */
    query: string;
}

// 专利过滤工具请求接口
export interface PatentFilterToolRequest {
    /** 任务ID */
    task_id: string;
    /** 操作类型 */
    operate_type?: string;
    /** 输入内容 */
    text?: string;
    /** 控制token消耗量 */
    cc_num?: number;
    /** 过滤索引，默认0 */
    filter_index?: number;
    /** 搜索索引，默认0 */
    search_index?: number;
}

// 专利过滤上下文接口
export interface PatentFilterContext {
    /** 当前检索总结果数 */
    total_count: number;
    /** 生成的查询语句 */
    generated_query: string;
    /** 专利文档列表 */
    patents: PatentDoc[];
    /** 过滤后的专利文档列表 */
    filtered_patents: PatentDoc[];
}


// 整体相似度分析接口
export interface OverallSimilarity {
    /** 整体相似度分数（0-1） */
    overall_score: number;
    /** 相似度等级 */
    similarity_level: PatentRiskLevel;
    /** 核心特征匹配数量 */
    core_feature_matches: number;
    /** 总特征数量 */
    total_features: number;
    /** 匹配特征数量 */
    matched_features: number;
    /** 相似度分析总结 */
    analysis_summary: string;
}

// 风险评估接口
export interface RiskAssessment {
    /** 侵权风险等级 */
    infringement_risk_level: PatentRiskLevel;
    /** 风险描述 */
    risk_description: string;
    /** 建议措施列表 */
    recommendations: string[];
    /** 规避设计建议列表 */
    design_around_suggestions: string[];
    /** 置信度（0-1） */
    confidence: number;
}

// 专利基本信息接口
export interface PatentInfo {
    /** 专利ID */
    patent_id: string;
    /** 专利号 */
    patent_number: string;
    /** 专利标题 */
    title: string;
    /** 申请人 */
    applicant: string;
    /** 申请日 */
    application_date: string;
    /** 法律状态 */
    legal_status: number[];
    /** PDF图片信息列表 */
    patent_images: PatentImage[];
}

/** 权利要求对比结果列表 */
export enum FeatureComparisonPublicValue {
    DIFFERENT = 0,
    QUESTIONABLE_RISK = 0.5,
    FWR = 0.9,
    SAME = 1,
}

export enum FeatureComparisonType {
    LITERAL_SAME = 'LITERAL_SAME',
    HYPERNYM = 'HYPERNYM',
    FUNCTIONAL = 'FUNCTIONAL',
    IMPLIED = 'IMPLIED',
    FWR = 'FWR',
    QUESTIONABLE_RISK = 'QUESTIONABLE_RISK',
    DIFFERENT = 'DIFFERENT',
}

export interface FeatureComparisonResult {
    claim_num:number;
    claim_feature:string;
    comparison_feature:string;
    /** 等同判定分值，后端可能返回字符串形式 */
    is_public: FeatureComparisonPublicValue | '0' | '0.5' | '0.9' | '1';
    is_text_similar?: 0 | 1;
    /** 等同类型 */
    type?: FeatureComparisonType;
    reason?: string;
}

export interface ClaimRisk {
    risk: boolean;
    claim_num: string;
    claim_type: 'dependent' | 'independent';
    /** 权利要求风险等级，新数据由后端返回；老数据可能没有 */
    risk_level?: PatentRiskLevel;
}

// 权利要求对比分析结果接口
export interface ClaimAnalysisResult {
    patent_id: string;
    /** 专利基本信息 */
    patent_info: PatentProfile;
    /** 权利要求对比结果列表 v2 */
    feature_comparisons: FeatureComparisonResult[];
    /** 字段名不对的情况 */
    feature_comparison_result: FeatureComparisonResult[];
    /** 整体相似度分析 */
    overall_similarity?: OverallSimilarity;
    /** 风险评估 */
    risk_assessment?: RiskAssessment;
    /** 对比结论 */
    conclusion:string;
    /** 技术特征总数，由后端按 feature_comparisons 条数统计 */
    total_features_count?: number;
    /** 公开/等同技术特征数，由后端按 is_public >= 0.9 统计 */
    public_features_count?: number;
    /** 是否在报告中选中 */
    include_in_report: boolean;
    /** 专利来源 */
    from_resource?: string;
    /** CC成功 */
    analysis_success?: boolean;
    /** 权利要求是否有风险 */
    claim_risks: {
        claims: ClaimRisk[];
        stats: {
            /** 独权数 */
            independent_claims_count: number;
            /** 风险独权数 */
            independent_claims_risk_count: number;
            /** 从权数 */
            dependent_claims_count: number;
            /** 风险从权数 */
            dependent_claims_risk_count: number;
        }
    }
    created_at?: string;
    updated_at?: string;
}

// 整体分析摘要接口
export interface OverallSummary {
    /** 分析的专利总数 */
    total_patents_analyzed: number;
    /** 高风险专利数量 */
    high_risk_patents_count: number;
    /** 中风险专利数量 */
    medium_risk_patents_count: number;
    /** 低风险专利数量 */
    low_risk_patents_count: number;
    /** 平均相似度分数 */
    average_similarity_score: number;
    /** 最高风险专利ID */
    highest_risk_patent_id: string;
    /** 整体建议列表 */
    overall_recommendations: string[];
}

// 最终检索结果接口
export interface PatentFinalResultWithClaimAnalysis {
    /** 专利列表 */
    // patents: PatentDoc[];
    /** 权利要求对比分析结果列表 */
    claim_analysis_results: ClaimAnalysisResult[];
    /** 整体分析摘要 */
    overall_summary: OverallSummary;
    /** 处理时间戳 */
    process_timestamp: number;
    /** 分析耗时（毫秒） */
    analysis_time_ms: number;
    /** 分析统计 */
    analysis_stats?: {
        failed_analyses: number;
        high_risk_patents: number;
        medium_risk_patents: number;
        low_risk_patents: number;
        total_patents: number;
        successful_analyses: number;
        unknown_risk_patents: number;
    }
}

// 专利搜索分析请求接口
export interface PatentSearchAnalyzeRequest {
    /** 任务ID */
    task_id: string;
    /** 搜索索引 */
    search_index: number;
    /** 专利ID */
    patent_id: string;
    /** 是否选中 */
    selected: boolean;
}

// 获取下一步检索类型请求接口
export interface NextStepRequest {
    /** 任务ID */
    taskId: string;
}

export type NextStepType =
    /** 特征抽取 */
    | 'FEATURE_EXTRACT'
    /** 关键词拓展 */
    | 'KEYWORD_EXPAND'
    /** 执行检索工具 */
    | 'SEARCH_TOOL'
    /** 执行过滤器 */
    | 'FILTER'
    /** 进入分析阶段 */
    | 'ANALYSIS'
    /** 完成 */
    | 'COMPLETED';

// 获取下一步检索类型响应接口
export interface NextStepResponse {
    /** 下一步任务类型 */
    next_step_type: NextStepType;
    /** 下一步任务ID */
    next_step_id: string;
    /** 推理过程 */
    reasoning_process: string;
}

// FTO打分请求接口
export interface FTOScoreRequest {
    /** 任务id */
    task_id: string;
    /** 评分值，范围 1-5 */
    score: number;
    /** 评分备注说明 */
    comment: string;
}

// FTO打分响应接口
export type FTOScoreResponse = FTOScoreRequest;


// SSE事件消息类型枚举
export enum MessageType {
    SELF = 'SELF', // 用户输入消息
    TODO = 'TODO', // 待办消息
    NORMAL = 'NORMAL', // 普通系统消息
    TASK = 'TASK', // 任务消息
    DATA = 'DATA', // 数据消息
    TASK_ACTION = 'TASK_ACTION', // 任务动作消息
    PUSH_MESSAGE = 'PUSH_MESSAGE', // 推送消息
    UPDATE_DATA = 'UPDATE_DATA', // 添加数据消息
    COMPLETE = 'TASK_COMPLETED', // 任务完成消息
    FAILED = 'TASK_FAILED', // 任务失败消息
    ABORTED = 'TASK_ABORTED', // 任务中止消息
    TASK_PING = 'TASK_PING', // 心跳消息
    RESUME = 'TASK_RESUME', // 任务继续消息
    INTERRUPTED = 'TASK_INTERRUPTED', // 任务用户操作中断消息
}

// SSE事件子类型枚举
export enum SubType {
    MESSAGE = 'MESSAGE', // 信息子类型
    MARKDOWN = 'MARKDOWN', // markdown子类型
    ACTION = 'ACTION', // 动作子类型
}

// 用于区别展示不同类型Action的图标、布局等
export enum ActionType {
    DEFAULT = 'DEFAULT',
    STEP_RECALL = 'STEP_RECALL',
    STEP_SCREENING = 'STEP_SCREENING',
    CC_PROGRESS_TABLE = 'CC_PROGRESS_TABLE',
}

// 基础SSE事件消息接口
export interface FtoSSEMessage<T = any> {
    /** 任务ID */
    task_id: string;
    /** 消息ID(自增) */
    id: number;
    /** 消息UUID */
    msg_id: string;
    /** 消息类型 */
    msg_type: MessageType;
    /** 子类型 */
    sub_type?: SubType | string;
    /** 分组ID */
    group_id?: string;
    /** 消息内容*/
    message?: string;
    /** 思考过程 */
    thinking?: string;
    /** 附加数据 */
    data: MessageData<T> | null;
    /** 创建时间 (ISO 8601格式) */
    created_at: string;
    /** 处理状态 */
    processed: 1 | 0 | -1;
    /** 目标消息ID */
    target_msg_id?: string;
    /** 检查点ID */
    checkpoint_id?: string;
}
export interface MessageData<T = any, A = any> extends Record<string, any> {
    data_type: DataType | string
    data: T

    action_type?: ActionType
    action?: A
}

export interface PatentSearchResponse {
    /** 检索式的名称 S1 S2 等*/
    search_name: string;
    /** 检索式 */
    query: string;
    /** 检索到的数据数量 */
    count: number;
}

export enum DataType {
    /** 提取特征 */
    EXTRACT_FEATURE = 'EXTRACT_FEATURE',
    /** 需确认的提取特征 */
    EXTRACT_FEATURE_WAITING_CONFIRM = 'EXTRACT_FEATURE_WAITING_CONFIRM',
    /** 扩展关键词 */
    EXPAND_KEYWORD = 'EXPAND_KEYWORD',
    /** 检索专利s0-s7 */
    KEYWORD_QUERY_PATENT_SEARCH = 'KEYWORD_QUERY_PATENT_SEARCH',
    /** 语义检索2个合1 */
    SUPPLEMENTARY_PATENT_SEARCH = 'SUPPLEMENTARY_PATENT_SEARCH',
    /** 检索专利 */
    PATENT_SEARCH = 'PATENT_SEARCH',
    /** 专利筛选 */
    PATENT_SCREENING = 'PATENT_SCREENING',
    /** 专利筛选结果 */
    PATENT_SCREENING_RESULT = 'PATENT_SCREENING_RESULT',
    /** 查全率/查准率 */
    PRECISION_AND_RECALL = 'PRECISION_AND_RECALL',
    /** FTO特征抽取*/
    FTO_CC = 'FTO_CC',
    /** FTO分析报告 */
    FTO_REPORT = 'FTO_REPORT'
}


// 对话流式请求参数接口
export interface DialogueStreamRequest {
    /** 任务ID */
    taskId: string;
    /** 是否重播 */
    replay?: boolean;
}

// 专利列表查询请求参数接口
export interface PatentListRequest {
    /** 查询语句 */
    q: string;
    /** 每页数量，默认20 */
    limit?: number;
    /** 当前页码，默认1 */
    page?: number;
    /** 是否返回总数 */
    with_count?: boolean;
    /** 视图类型 */
    view_type?: string;
    /** 是否异步 */
    use_async?: boolean;
}

// 专利图像信息接口
export interface PatentImage {
    /** 图片标签 */
    labels: string[];
    /** 是否已提取 */
    extracted: boolean;
    /** 专利ID */
    patent_id: string;
    /** 图片标题代码 */
    fig_title_code: string;
    /** 缩略图URL */
    fulltext_image240_url: string;
    /** 官方尺寸 */
    official_size: string;
    /** 图片ID */
    image_id: string;
    /** 图片来源 */
    image_from: string;
    /** 存储路径 */
    storage_path: string;
    /** 源图片类型 */
    source_image_type: string;
    /** 图片类型 */
    image_type: string;
    /** 是否已提取 */
    is_extracted: boolean;
}

// 专利申请人信息接口
export interface PatentApplicant {
    /** 英文名称 */
    EN: string[];
    /** 官方名称 */
    OFFICIAL: string[];
}

// 专利详细数据接口（基于mock数据结构）
export interface PatentDetailData {
    /** 标题语言 */
    TITLE_LANG: string;
    /** 同族国家 */
    FAMILY_COUNTRY: string;
    /** PDF图片数量 */
    PDF_IMAGE_COUNT: number;
    /** 发明人列表 */
    IN: string[];
    /** PDF图片信息列表 */
    PDF_IMAGE: PatentImage[];
    /** 是否有许可 */
    HAS_LICENSE: boolean;
    /** 法律状态数组 */
    LEGAL_STATUS: number[];
    /** 公开日 */
    PBD: string;
    /** 申请日 */
    APD: string;
    /** 阅读状态 */
    READ_STATUS: boolean;
    /** 当前申请人 */
    ANC: PatentApplicant;
    /** 发明人语言 */
    IN_LANG: string;
    /** 专利ID */
    PATENT_ID: string;
    /** 专利标题（可能包含高亮标签） */
    TITLE: string;
    /** 是否有标准 */
    HAS_STANDARD: boolean;
    /** 专利号 */
    PN: string;
    /** 标题支持的语言列表 */
    TITLE_LANGS: string[];
    /** 是否有诉讼 */
    HAS_LITIGATION: boolean;
}

// 专利数量统计接口
export interface PatentCountInfo {
    /** 分组数量 */
    group_count: number;
    /** 总数量 */
    total_count: number;
}

// 专利搜索参数接口
export interface PatentSearchParams {
    /** 查询语句 */
    q: string;
    /** 配置文件 */
    profile: string;
    /** 字段列表 */
    fields: string[];
    /** 排序规则 */
    sort: Array<{
        field: string;
        order: 'asc' | 'desc';
    }>;
    /** 词干处理 */
    stem: boolean;
    /** 折叠规则 */
    collapse: {
        field: string;
        rules: Array<{
            type: string;
            field: string;
        }>;
    };
    /** 其他参数 */
    patsnap: boolean;
    result_key: boolean;
    start: number;
    rows: number;
    timeout: number;
    rd: boolean;
    outline: boolean;
    patsnap_flag: boolean;
    highlight: Record<string, any>;
    use_async: boolean;
    use_mq: boolean;
    export: boolean;
    family_search: boolean;
    ultra_long_query: boolean;
}

// 专利列表响应接口
export interface PatentListResponse {
    /** 关键词列表 */
    keywords: string[][];
    /** 专利数据列表 */
    patent_data: PatentDetailData[];
    /** 专利数量统计 */
    patent_count: PatentCountInfo;
    /** 同族关键词 */
    family_keywords: string[];
    /** 搜索参数 */
    search_params: PatentSearchParams;
}

export enum ImportFromWorkspaceStatus {
    Pending = 'pending',
    Running = 'running',
    Completed = 'completed',
    Failed = 'failed',
    Cancelled = 'cancelled',
}

export interface ImportFromWorkspaceResponse {
    cc_batch_id: string;
    task_id: string;
    folder_id: string;
    status: ImportFromWorkspaceStatus;
    patent_count: number;
    cc_success_patents: string[];
    cc_failed_patents: string[];
    progress: number;
    error_message: string;
}

export interface WorkspaceInfo {
    folder_id: string;
    workspace_id: string;
    folder_path: string;
}

export interface WorkspaceInfoResponse {
    task_id: string;
    workspace_info: WorkspaceInfo[]
}

export interface ProcessAddingPatentRequest {
    patent_id: string
}

export interface ExportReportAsWordResponse {
    file_name: string;
    sign_url: string;
    s3_key: string;
}

// 上传进度回调函数类型
export type UploadProgressCallback = (progress: {
    /** 已上传字节数 */
    loaded: number;
    /** 总字节数 */
    total: number;
    /** 上传进度百分比 (0-100) */
    percentage: string;
}) => void;

// 上传图片响应接口
export interface UploadImagesResponse {
    /** S3文件键列表 */
    file_keys: string[];
    /** 签名URL列表 */
    signed_urls: string[];
    /** 上传成功的文件数量 */
    uploaded_count: number;
}

// 取消任务请求接口
export interface CancelTaskRequest {
    /** 任务ID */
    task_id: string;
    /** 任务类型，固定值为 analysis */
    task_type: 'analysis';
}

// 取消任务响应数据接口
export interface CancelTaskResponse {
    /** 任务ID */
    task_id: string;
    /** 任务类型 */
    task_type: string;
    /** 后台任务ID */
    background_task_id: string;
    /** 是���已取消 */
    cancelled: boolean;
}

// 继续任务请求接口
export interface ResumeTaskRequest {
    /** 消息ID，从指定消息恢复 */
    message_id?: string;
    /** 检查点ID，从指定检查点恢复 */
    checkpoint_id?: string;
    /** 更新状态 */
    update_state?: Record<string, any>;
    /** 是否跳过缓存 */
    no_cache?: boolean;
    /** 操作类型，分编辑和确认，必须有update_state */
    operation_type?: 'edit' | 'confirm';
    /** 重新执行的起始节点名称（如 'patent_screening'） */
    restart_node?: string;
}

// 继续任务响应接口
export interface ResumeTaskResponse {
    /** 任务ID */
    task_id: string;
    /** 任务状态 */
    status: string;
    /** 响应消息 */
    message: string;
    /** 后台任务ID */
    background_task_id: string;
    /** 任务结果 */
    result: any;
    /** 创建时间 (ISO 8601格式) */
    created_at: string;
    /** 完成时间 (ISO 8601格式) */
    completed_at: string | null;
    /** 错误信息 */
    error: string | null;
}

// 分类助手项接口
export interface HelperSectionItem {
    /** 分类ID */
    id: string;
    /** 是否有子节点 */
    has_children: boolean;
    /** 多语言描述映射 */
    lang_desc_map: {
        /** 中文描述 */
        cn: string;
        /** 英文描述 */
        en: string;
    };
    children?: HelperSectionItem[]
}

// 分类助手响应接口
export type HelperSectionResponse = HelperSectionItem[]

// 分类助手子分类请求接口
export interface HelperSubclassRequest {
    /** 分类类型，如 CPC */
    type: string;
    /** 分类代码，如 B */
    code?: string;
    /** 层级 */
    level: number;
    /** 起始位置，默认 0 */
    start?: number;
}

// 分类助手子分类响应接口
export type HelperSubclassResponse = HelperSectionItem[]

// 分类助手搜索请求接口
export interface HelperSearchRequest {
    /** 搜索关键词 */
    q: string;
    /** 分类类型，如 CPC */
    type: string;
    /** 起始位置，默认 0 */
    start?: number;
}

// 分类助手搜索查询结果接口
export interface HelperSearchQueryResult {
    /** 分类树 */
    cls_tree: HelperSectionItem;
}

// 分类助手搜索响应接口
export interface HelperSearchResponse {
    /** 查询结果列表 */
    query_results: HelperSearchQueryResult[];
    /** 高亮词 */
    h_words: string[];
}

export interface SignFilesResponse {
    signed_files: {
        s3_key: string;
        signed_url: string;
    }[];
}

export interface WorkspaceFolderPatentsRequest {
    q?: Record<string, any>;
    qid?: string;
    efqid?: string;
    cond?: string;
    sort?: string;
    layer_id?: string;
    view_type?: string;
    field_id?: string;
    ai_field_id?: string;
    start?: number;
    end?: number;
    select_mode?: 'INCLUDE' | 'EXCLUDE' | 'RANGE' | 'NONE';
    patent_ids?: string[];
    refiner?: Record<string, any>;
}

export const FtoProApi = Symbol.for('FtoProApi');

/**
 * @description 用于抽取技术方案特征，生成FTO分析结果
 */
export interface FtoProApi {
    createTask(data: CreateTaskRequest): Promise<CreateTaskResponse>;
    /**
     * 抽取技术方案特征
     * @param data 特征抽取请求参数
     * @returns FTO分析结果
     */
    extractFeature(data: ExtractFeatureRequest): Promise<ExtractFeatureResult>;

    /**
     * 生成专利检索查询语句
     * @param data 专利搜索工具请求参数
     * @returns 生成的检索式
     */
    getSearchQuery(data: PatentSearchToolRequest): Promise<string>;

    /**
     * 执行专利搜索
     * @param data 专利搜索工具请求参数
     * @returns 专利搜索响应结果
     */
    search(data: PatentSearchToolRequest): Promise<PatentSearchResponse>;

    /**
     * 用户自定义检索
     * @param data 自定义检索请求参数
     * @returns 专利搜索响应结果
     */
    customSearch(data: CustomSearchRequest): Promise<PatentSearchResponse>;

    /**
     * 执行专利过滤
     * @param data 专利过滤工具请求参数
     * @returns 专利过滤上下文
     */
    filter(data: PatentFilterToolRequest): Promise<PatentFilterContext>;

    /**
     * 获取最终检索结果
     * @param data 基础请求参数
     * @returns 包含权利要求对比分析的最终检索结果
     */
    getFinalResult(data: ExtractFeatureRequest): Promise<PatentFinalResultWithClaimAnalysis>;

    /**
     * 分析指定专利
     * @param data 专利搜索分析请求参数
     * @returns 分析结果
     */
    analyzePatent(data: PatentSearchAnalyzeRequest): Promise<ClaimAnalysisResult>;

    /**
     * 获取下一步检索类型
     * @param data 获取下一步检索类型请求参数
     * @returns 下一步检索类型响应结果
     */
    getNextStep(data: NextStepRequest): Promise<NextStepResponse>;

    /**
     * 获取拓展关键词
     * @param data 关键词拓展请求参数
     * @returns 关键词拓展响应结果
     */
    expandedKeywords(data: ExpandedKeywordsRequest): Promise<ExpandedKeywordsResponse>;

    /**
     * 批量关键词补齐
     * @param data 批量关键词补齐请求参数
     * @returns 批量关键词补齐响应结果
     */
    completeKeywords(data: CompleteKeywordsRequest): Promise<CompleteKeywordsResponseData>;

    /**
     * 获取对话流式消息
     * @param data 对话流式请求参数
     * @returns SSE流对象
     */
    getDialogueStream(data: DialogueStreamRequest): Promise<PsHttpSSEOfficial<FtoSSEMessage<any>>>;

    /**
     * 获取专利列表
     * @param data 专利列表查询请求参数
     * @returns 专利列表响应结果
     */
    getPatentList(data: PatentListRequest): Promise<PatentListResponse>;
    /**
     * 获取专利数量
     * @param data 专利列表查询请求参数
     * @returns 专利数量响应结果
     */
    getPatentCount(data: PatentListRequest): Promise<PatentCountResponse>;

    /**
     * 保存FTO分析结果
     * @param data 项目请求参数
     * @returns FTO分析结果
     */
    saveProject(data: FtoProjectRequest): Promise<FtoProjectResponse>;

    /**
     * 获取FTO项目信息
     * @param taskId 任务ID
     * @returns FTO项目信息
     */
    getProject(taskId: string): Promise<FtoProjectResponse>;

    /**
     * 获取检索式列表
     * @param taskId 任务ID
     * @returns 检索式列表响应
     */
    getRetrievalQueries(taskId: string): Promise<RetrievalQueriesResponse>;

    /**
     * 创建 FTO 报告
     * @param taskId - 任务ID
     * @returns Promise<FtoReportResponse> - 返回创建的 FTO 报告信息
     */
    createReport(taskId: string): Promise<FtoReportResponse>;

    /**
     * 获取 FTO 报告列表
     * @param data - 报告列表查询参数
     * @returns Promise<FtoReportListResponse> - 返回 FTO 报告列表
     */
    getReportList(data: FtoReportListRequest): Promise<FtoReportListResponse>;

    /**
     * 获取指定 FTO 报告详情
     * @param reportId - 报告ID
     * @returns Promise<FtoReportResponse> - 返回 FTO 报告详细信息
     */
    getReport(reportId: string): Promise<FtoReportResponse>;

    /**
     * FTO 打分接口
     * @param data - FTO打分请求参数
     * @returns Promise<FTOScoreResponse> - 返回 FTO 打分响应结果
     */
    ftoScore(data: FTOScoreRequest): Promise<FTOScoreResponse>;
    /**
     * 检查任务是否存在
     * @param data - 检查任务是否存在请求参数
     * @returns Promise<TaskExistsResponse> - 返回检查任务是否存在响应结果
     */
    taskExists(data: TaskExistsRequest): Promise<TaskExistsResponse>;

    /**
     * 从workspace导入专利
     * @param taskId - 任务id
     * @param folderId - 工作空间文件夹id
     * @param signal - 取消信号
     * @returns Promise<ImportFromWorkspaceResponse> - 导入任务信息
     */
    importFromWs(taskId: string, folderId: string, signal?: AbortSignal): Promise<ImportFromWorkspaceResponse>;

    /**
     * 检查从workspace导入任务状态
     * @param taskId - 任务id
     * @param batchId - 工作空间文件夹id
     * @param signal - 取消信号
     * @returns Promise<ImportFromWorkspaceResponse> - 导入任务信息
     */
    importFromWsStatusCheck(taskId: string, batchId: string, signal?: AbortSignal): Promise<ImportFromWorkspaceResponse>;

    /**
     * 更新fto关联的ws
     * @param taskId - 任务id
     * @param ws - ws信息
     * @returns Promise<any>
     */
    updateWorkspaceInfo(taskId: string, ws: WorkspaceInfo): Promise<any>

    /**
     * 查询fto关联的ws
     * @param taskId - 任务id
     * @returns Promise<WorkspaceInfo> - 关联的ws
     */
    getWorkspaceInfo(taskId: string): Promise<WorkspaceInfoResponse>

    /**
     * 更新权利要求对比分析结果
     * @param taskId - 任务id
     * @param patentId - 专利id
     * @param analysis - 权利要求对比分析结果
     */
    updateClaimAnalysisResult(taskId: string, patentId: string, analysis: ClaimAnalysisResult): Promise<ClaimAnalysisResult>;

    /**
     * 查询权利要求对比分析结果
     * @param taskId - 任务id
     */
    getClaimAnalysisResults(taskId: string): Promise<ClaimAnalysisResult[]>;

    /**
     * 增加单篇专利
     * @param taskId - 任务id
     * @param request - 增加专利请求参数
     */
    processAddingPatent(taskId: string, request: ProcessAddingPatentRequest): Promise<ImportFromWorkspaceResponse>

    /**
     * 上传图片文件
     * @param files - 图片文件列表
     * @param onProgress - 可选的上传进度回调函数
     * @returns Promise<UploadImagesResponse> - 返回上传结果，包含文件键和签名URL
     */
    uploadImages(files: (File | Blob)[], onProgress?: UploadProgressCallback): Promise<UploadImagesResponse>

    /**
     * 图片文件签名
     */
    signFiles(keys: string[]): Promise<SignFilesResponse>

    /**
     * 导出报告为Word文件
     * @param reportId - 报告ID
     */
    exportReportAsWord(reportId: string): Promise<ExportReportAsWordResponse>

    /**
     * 取消分析任务
     * @param data - 取消任务请求参数
     * @returns Promise<CancelTaskResponse> - 返回取消任务响应结果
     */
    cancelTask(data: CancelTaskRequest): Promise<CancelTaskResponse>

    /**
     * 继续分析任务
     * @param taskId - 任务ID
     * @param data - 恢复任务请求参数（可选）
     * @returns Promise<ResumeTaskResponse> - 返回继续任务响应结果
     */
    resumeTask(taskId: string, data?: ResumeTaskRequest): Promise<ResumeTaskResponse>

    /**
     * 获取分类助手分区列表
     * @param type - 分类类型，如 CPC、IPC 等
     * @returns Promise<HelperSectionResponse> - 返回分类助手分区列表
     */
    getHelperSection(type: string): Promise<HelperSectionResponse>

    /**
     * 获取分类助手子分类列表
     * @param data - 子分类请求参数
     * @returns Promise<HelperSubclassResponse> - 返回子分类列表
     */
    getHelperSubclass(data: HelperSubclassRequest): Promise<HelperSubclassResponse>

    /**
     * 搜索分类助手
     * @param data - 搜索请求参数
     * @returns Promise<HelperSearchResponse> - 返回搜索结果
     */
    searchHelper(data: HelperSearchRequest): Promise<HelperSearchResponse>

    /**
     * 获取工作空间文件夹下的专利ID列表
     * @param folderId - 文件夹ID
     * @param params - 可选的过滤参数
     * @returns Promise<string[]> - 专利ID列表
     */
    getFolderPatents(folderId: string, params?: WorkspaceFolderPatentsRequest): Promise<string[]>
}
